import { useCallback, useState } from "react";
import { ApiError, type PartnerRefIdResponse } from "@dearbill/shared";
import { useApi } from "../auth/useApi";

/**
 * Referral-mint action. `POST /api/partner/orgs/{orgId}/refid` mints a
 * one-patient referral link (single-use, short-lived), and `mint()` returns its
 * URL without opening anything. `ReferSomeoneModal` uses it only for the
 * staff-assisted "open the form on this device" path, minting on click so that
 * opening the modal burns no link.
 *
 * `mint()` resolves to the minted URL, or `null` on failure. A 401 sets no
 * `error`: `useApi` already cleared the session, so the login redirect takes
 * over. Any other failure sets `error` to copy that says what to do next.
 */
export function useReferralMint(orgId: string): {
  mint: () => Promise<string | null>;
  minting: boolean;
  error: string | null;
} {
  const api = useApi();
  const [minting, setMinting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mint = useCallback(async (): Promise<string | null> => {
    setMinting(true);
    setError(null);
    try {
      const minted = await api<PartnerRefIdResponse>(`/api/partner/orgs/${orgId}/refid`, {
        method: "POST",
      });
      return minted.url;
    } catch (err: unknown) {
      // A 401 already cleared the session in `useApi`, and the redirect takes over.
      if (!(err instanceof ApiError && err.status === 401)) {
        setError("We couldn't start a new referral. Please try again.");
      }
      return null;
    } finally {
      setMinting(false);
    }
  }, [api, orgId]);

  return { mint, minting, error };
}
